import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, Smartphone, Mail, Lock, ArrowRight, Building2, ArrowLeft } from 'lucide-react';
import logo from '../assets/valuate-logo.png';
import loginImage from '../assets/hero.jpeg';

export default function RequestAccessPage() {
  const [formData, setFormData] = useState({
    upi: "",
    ownerIdNumber: "",
    phone: "",
    // password: '',
    // confirmPassword: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeInput, setActiveInput] = useState('');
  const navigate = useNavigate();

  // Using environment variable for API URL with fallback
  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://valuate-api.vercel.app/api/v1';

  // Theme styles (simplified for this example)
  const themeStyles = {
    bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
    cardBg: "bg-white",
    textColor: "text-gray-800",
    inputBg: "bg-white",
    borderColor: "border-gray-200",
    buttonBg: "bg-gradient-to-r from-blue-600 to-indigo-600",
    buttonHover: "hover:from-blue-700 hover:to-indigo-700",
    imageBg: "bg-gradient-to-br from-blue-600 to-indigo-700",
    labelColor: "text-gray-600",
    linkColor: "text-blue-600",
  };

  // UPI validation function for format "4/01/16/05/6577"
  const isValidUPI = (upi: string): boolean => {
    const upiPattern = /^\d{1}\/\d{2}\/\d{2}\/\d{2}\/\d{4,6}$/;
    return upiPattern.test(upi);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    // Validate all fields
    if (!formData.upi || !formData.ownerIdNumber || !formData.phone) {
      setError("All fields are required");
      setIsLoading(false);
      return;
    }

    // Validate UPI format
    if (!isValidUPI(formData.upi)) {
      setError("UPI must be in the format: 4/01/16/05/6577");
      setIsLoading(false);
      return;
    }

    try {
      // Prepare the payload
      const payload = {
        upi: formData.upi.trim(),
        ownerIdNumber: formData.ownerIdNumber.trim(),
        phone: formData.phone.replace(/\D/g, ""),
      };

      // Send request to API
      const response = await fetch(`${apiUrl}/auth/requestaccess`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Registration failed");
      }

      // Success handling
      setSuccess(
        "Request submitted successfully! You will be notified once approved."
      );

      // Reset form
      setFormData({
        upi: "",
        ownerIdNumber: "",
        phone: "",
        // password: '',
        // confirmPassword: '',
      });

      // Redirect after delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen ${themeStyles.bgColor} flex items-center justify-center p-4`}
    >
      <div
        className={`w-full max-w-5xl ${themeStyles.cardBg} rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row`}
      >
        {/* Left side with branding */}
        <div
          className={`w-full md:w-1/2 ${themeStyles.imageBg} flex items-center justify-center p-4 relative overflow-hidden`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 to-indigo-900/70 z-10"></div>
          <div className="relative z-20 max-w-md text-white text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm p-3 rounded-xl mb-3">
                <img src={logo} alt="Valuatify Logo" className="h-8" />
              </div>

              <h1 className="text-2xl font-bold mb-1">Valuate</h1>
              <p className="text-blue-100 text-sm">Property Valuation System</p>
            </div>

            <div className="w-full h-32 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg shadow-lg mb-4 flex items-center justify-center">
              <img
                src={loginImage}
                alt="Property Valuation"
                className="w-full h-32 object-cover rounded-lg shadow-lg mb-4"
              />
            </div>

            <div className="mb-3">
              <div className="flex items-center justify-center mb-1">
                <Building2 className="h-5 w-5 mr-1" />
                <h3 className="text-lg font-semibold">Request Access</h3>
              </div>
              <p className="text-blue-100 text-xs">
                Submit your details for account approval
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                { icon: Shield, text: "Secure" },
                { icon: Smartphone, text: "Verified" },
                { icon: Lock, text: "Protected" },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded p-1 text-center"
                >
                  <item.icon className="h-3 w-3 mx-auto" />
                  <p className="text-xs">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right side with form */}
        <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center mb-4 ${themeStyles.linkColor} hover:underline self-start`}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start text-sm">
              <svg
                className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-start text-sm">
              <svg
                className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                className={`block mb-1 text-sm font-medium ${themeStyles.labelColor}`}
              >
                UPI Number
              </label>
              <div
                className={`relative rounded-lg transition-all duration-200 ${
                  activeInput === "upi"
                    ? "ring-2 ring-blue-500 ring-opacity-50"
                    : ""
                }`}
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="upi"
                  value={formData.upi}
                  onChange={handleChange}
                  onFocus={() => setActiveInput("upi")}
                  onBlur={() => setActiveInput("")}
                  className={`w-full pl-9 pr-3 py-2.5 rounded-lg text-sm ${themeStyles.inputBg} border ${themeStyles.borderColor} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                  placeholder="Format: 4/01/16/05/6577"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Format: 4/01/16/05/6577
              </p>
            </div>

            <div>
              <label
                className={`block mb-1 text-sm font-medium ${themeStyles.labelColor}`}
              >
                Phone Number
              </label>
              <div
                className={`relative rounded-lg transition-all duration-200 ${
                  activeInput === "phone"
                    ? "ring-2 ring-blue-500 ring-opacity-50"
                    : ""
                }`}
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Smartphone className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setActiveInput("phone")}
                  onBlur={() => setActiveInput("")}
                  className={`w-full pl-9 pr-3 py-2.5 rounded-lg text-sm ${themeStyles.inputBg} border ${themeStyles.borderColor} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                  placeholder="Phone Number"
                  required
                />
              </div>
            </div>

            <div>
              <label
                className={`block mb-1 text-sm font-medium ${themeStyles.labelColor}`}
              >
                National ID
              </label>
              <div
                className={`relative rounded-lg transition-all duration-200 ${
                  activeInput === "ownerIdNumber"
                    ? "ring-2 ring-blue-500 ring-opacity-50"
                    : ""
                }`}
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="ownerIdNumber"
                  value={formData.ownerIdNumber}
                  onChange={handleChange}
                  onFocus={() => setActiveInput("ownerIdNumber")}
                  onBlur={() => setActiveInput("")}
                  className={`w-full pl-9 pr-3 py-2.5 rounded-lg text-sm ${themeStyles.inputBg} border ${themeStyles.borderColor} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                  placeholder="National ID"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 ${themeStyles.buttonBg} ${
                themeStyles.buttonHover
              } text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg text-sm ${
                isLoading
                  ? "opacity-80 cursor-not-allowed"
                  : "hover:scale-[1.02]"
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Request Access
                  <ArrowRight className="w-3 h-3 ml-2" />
                </>
              )}
            </button>
          </form>

          <div className={`mt-6 pt-4 border-t ${themeStyles.borderColor}`}>
            <p className={`text-xs text-center ${themeStyles.labelColor}`}>
              Already have access?{" "}
              <button
                onClick={() => navigate("/login")}
                className={`font-medium ${themeStyles.linkColor} hover:underline transition-colors`}
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
